import React from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

const useSharedStyles = makeStyles((theme) => ({
  metricCard: {
    borderRadius: 18,
    minHeight: 138,
    boxShadow: '0 14px 30px rgba(31, 55, 42, 0.08)',
    border: '1px solid rgba(205, 216, 209, 0.9)'
  },
  metricEyebrow: {
    fontSize: '0.72rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#5d6c64'
  },
  metricValue: {
    fontSize: '2rem',
    fontWeight: 700,
    lineHeight: 1.1,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  metricCaption: {
    color: '#5d6c64'
  },
  stateCard: {
    borderRadius: 20,
    boxShadow: '0 14px 30px rgba(31, 55, 42, 0.08)',
    border: '1px solid rgba(205, 216, 209, 0.9)'
  },
  stateCardNeutral: {
    background: 'linear-gradient(180deg, rgba(249, 251, 248, 0.96) 0%, rgba(244, 248, 245, 0.96) 100%)'
  },
  stateCardDanger: {
    background: 'linear-gradient(180deg, rgba(255, 247, 245, 0.98) 0%, rgba(255, 241, 238, 0.96) 100%)',
    borderColor: 'rgba(235, 189, 182, 0.95)'
  },
  stateCardMuted: {
    background: 'linear-gradient(180deg, rgba(244, 247, 251, 0.98) 0%, rgba(238, 243, 249, 0.98) 100%)',
    borderColor: 'rgba(203, 215, 229, 0.95)'
  },
  stateContent: {
    minHeight: 220,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(4)
  },
  stateTitle: {
    fontWeight: 700,
    color: '#21352d'
  },
  stateMessage: {
    maxWidth: 560,
    color: '#5d6c64'
  }
}))

const METRIC_ACCENTS = {
  green: '#154734',
  gold: '#8a692d',
  blue: '#23417e',
  plum: '#6a1b9a',
  red: '#b3261e'
}

const STATUS_STYLES = {
  overdue: { backgroundColor: '#fdecea', color: '#b3261e' },
  never_done: { backgroundColor: '#fff3d9', color: '#8c5b00' },
  due_soon: { backgroundColor: '#fff1d6', color: '#946200' },
  needs_completion: { backgroundColor: '#f1e7ff', color: '#6a1b9a' },
  ok: { backgroundColor: '#e5f4ea', color: '#1b5e20' },
  ignored: { backgroundColor: '#eceff1', color: '#455a64' }
}

const ACCESS_STATUS_META = {
  active: { label: 'Active', style: { backgroundColor: '#e5f4ea', color: '#1b5e20' } },
  invited: { label: 'Invited', style: { backgroundColor: '#eef2ff', color: '#3547a5' } },
  suspended: { label: 'Suspended', style: { backgroundColor: '#fff1d6', color: '#946200' } },
  offboarded: { label: 'Offboarded', style: { backgroundColor: '#fdecea', color: '#b3261e' } }
}

function isValidDate (value) {
  if (!value) {
    return false
  }

  const date = new Date(value)
  return !Number.isNaN(date.getTime())
}

export function formatPortalDate (value, options = {}) {
  const {
    fallback = 'Not scheduled',
    includeWeekday = false
  } = options

  if (!isValidDate(value)) {
    return fallback
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...(includeWeekday ? { weekday: 'short' } : {})
  }).format(new Date(value))
}

export function formatPortalDateTime (value, options = {}) {
  const { fallback = 'No activity recorded' } = options

  if (!isValidDate(value)) {
    return fallback
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(new Date(value))
}

export function getStatusChipStyle (status) {
  return STATUS_STYLES[status] || { backgroundColor: '#eceff1', color: '#37474f' }
}

export function getAccessStatusMeta (status) {
  return ACCESS_STATUS_META[status] || {
    label: status ? status.replace(/_/g, ' ') : 'Unknown',
    style: { backgroundColor: '#eceff1', color: '#37474f' }
  }
}

export function PortalMetricCard ({ eyebrow, value, caption, accent = 'green' }) {
  const classes = useSharedStyles()

  return (
    <Card className={classes.metricCard}>
      <CardContent>
        <Typography className={classes.metricEyebrow}>{eyebrow}</Typography>
        <Typography className={classes.metricValue} style={{ color: METRIC_ACCENTS[accent] || METRIC_ACCENTS.green }}>
          {value}
        </Typography>
        <Typography variant="body2" className={classes.metricCaption}>
          {caption}
        </Typography>
      </CardContent>
    </Card>
  )
}

export function PortalStateCard ({
  title,
  message,
  actionLabel,
  onAction,
  tone = 'neutral',
  minHeight = 220,
  children
}) {
  const classes = useSharedStyles()
  const toneClass = tone === 'danger'
    ? classes.stateCardDanger
    : tone === 'muted'
      ? classes.stateCardMuted
      : classes.stateCardNeutral

  return (
    <Card className={`${classes.stateCard} ${toneClass}`}>
      <CardContent className={classes.stateContent} style={{ minHeight }}>
        <Typography variant="h6" className={classes.stateTitle}>
          {title}
        </Typography>
        {message ? (
          <Typography variant="body2" className={classes.stateMessage}>
            {message}
          </Typography>
        ) : null}
        {children}
        {actionLabel && onAction ? (
          <Box mt={1}>
            <Button variant="outlined" color="primary" onClick={onAction}>
              {actionLabel}
            </Button>
          </Box>
        ) : null}
      </CardContent>
    </Card>
  )
}

export function PortalLoadingCard ({
  title = 'Loading portal data',
  message = 'Pulling the latest subcontractor information now.',
  minHeight = 240
}) {
  return (
    <PortalStateCard title={title} message={message} tone="muted" minHeight={minHeight}>
      <CircularProgress size={28} />
    </PortalStateCard>
  )
}
